<template>
    <div class="mt-md relative w-full">
        <div class="slider-track" :style="sliderStyle"></div>
        <input v-model="minSliderVal" :disabled="disabled" type="range" :min="min" :max="max" />
        <input v-model="maxSliderVal" :disabled="disabled" type="range" :min="min" :max="max" />
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
    min: { type: Number, required: true },
    max: { type: Number, required: true },
    overrideMin: Number,
    overrideMax: Number,
    disabled: Boolean,
});

const emit = defineEmits(['override']);
const minSliderVal = ref<number>(props.overrideMin ?? 0);
const maxSliderVal = ref<number>(props.overrideMax ?? 100);
const lowerSlider = computed(() => {
    return ((minSliderVal.value - props.min) / (props.max - props.min)) * 100;
});
const upperSlider = computed(() => ((maxSliderVal.value - props.min) / (props.max - props.min)) * 100);

const sliderStyle = computed(() => {
    return {
        background: `
        linear-gradient(
            to right,
            var(--state-info-border) ${lowerSlider.value}%,
            var(--btn-primary-bg) ${lowerSlider.value}%,
            var(--btn-primary-bg) ${upperSlider.value}%,
            var(--state-info-border) ${upperSlider.value}%
        )`,
    };
});

watch(minSliderVal, (newVal) => {
    if (newVal >= maxSliderVal.value) {
        maxSliderVal.value = Math.min(minSliderVal.value, props.max);
    }
    emit('override', [minSliderVal.value, maxSliderVal.value]);
});

watch(maxSliderVal, (newVal) => {
    if (newVal <= minSliderVal.value) {
        minSliderVal.value = Math.max(maxSliderVal.value, props.min);
    }
    if (minSliderVal.value < props.min) {
        minSliderVal.value = props.min;
    }
    emit('override', [minSliderVal.value, maxSliderVal.value]);
});

watch(
    () => props.overrideMin,
    (newVal) => {
        minSliderVal.value = newVal ?? 0;
    },
);

watch(
    () => props.overrideMax,
    (newVal) => {
        maxSliderVal.value = newVal ?? 100;
    },
);
</script>

<style scoped>
@reference '@/assets/styles/default.css';

input[type='range'] {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    width: 100%;
    outline: none;
    position: absolute;
    margin: auto;
    top: 0;
    bottom: 0;
    background-color: transparent;
    pointer-events: none;
}
.slider-track {
    width: 100%;
    height: 5px;
    position: absolute;
    margin: auto;
    top: 0;
    bottom: 0;
    border-radius: 5px;
}
input[type='range']::-webkit-slider-runnable-track {
    -webkit-appearance: none;
    height: 5px;
}
input[type='range']::-moz-range-track {
    -moz-appearance: none;
    height: 5px;
}
input[type='range']::-ms-track {
    appearance: none;
    height: 5px;
}
input[type='range']::-webkit-slider-thumb {
    -webkit-appearance: none;
    height: 1.7em;
    width: 1.7em;
    background-color: white;
    border: 1px solid var(--border-strong);
    cursor: pointer;
    margin-top: -9px;
    pointer-events: auto;
    border-radius: 50%;
}
input[type='range']::-moz-range-thumb {
    -webkit-appearance: none;
    height: 1.7em;
    width: 1.7em;
    cursor: pointer;
    border-radius: 50%;
    background-color: white;
    border: 1px solid var(--border-strong);
    pointer-events: auto;
}
input[type='range']::-ms-thumb {
    appearance: none;
    height: 1.7em;
    width: 1.7em;
    cursor: pointer;
    border-radius: 50%;
    background-color: white;
    border: 1px solid var(--border-strong);
    pointer-events: auto;
}
input[type='range']:active::-webkit-slider-thumb {
    background-color: var(--bg-lightest);
    border: 1px solid var(--border-strong);
}
.values {
    background-color: var(--btn-primary-bg);
    width: 32%;
    position: relative;
    margin: auto;
    padding: 10px 0;
    border-radius: 5px;
    text-align: center;
    font-weight: 500;
    font-size: 25px;
    color: var(--text-on-accent);
}
.values:before {
    content: '';
    position: absolute;
    height: 0;
    width: 0;
    border-top: 15px solid var(--btn-primary-bg);
    border-left: 15px solid transparent;
    border-right: 15px solid transparent;
    margin: auto;
    bottom: -14px;
    left: 0;
    right: 0;
}
</style>
